import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  TrendingUp,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowRight,
  Plug,
  CreditCard,
} from "lucide-react";
import { hubSpotService } from "../../services/HubSpotService";
import { stripeIntegrationService } from "../../services/StripeIntegrationService";
import { useToastContext } from "../../context/ToastContext";
import { useLoader } from "../../context/LoaderContext";
import { useTheme } from "../../context/ThemeContext";
import hubspotLogo from "../../assets/hubspot/hubspot_orange.png";
import stripeLogoPurple from "../../assets/stripe/stripe_purple.png";
import stripeLogoWhite from "../../assets/stripe/stripe_white.png";

type Step = "check" | "select" | "hubspot" | "stripe" | "complete";

const ContractIntelligenceSetup: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToastContext();
  const { showLoader, hideLoader } = useLoader();
  const { theme } = useTheme();

  const [currentStep, setCurrentStep] = useState<Step>("check");
  const [hubspotConnected, setHubspotConnected] = useState<boolean>(false);
  const [stripeConnected, setStripeConnected] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(true);
  const [isConnectingHubspot, setIsConnectingHubspot] =
    useState<boolean>(false);
  const [isConnectingStripe, setIsConnectingStripe] = useState<boolean>(false);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);

  useEffect(() => {
    checkConnections();
  }, []);

  // Re-check connections when component mounts (e.g., after OAuth redirect)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hubspotSuccess = urlParams.get("hubspot_success");
    const stripeSuccess = urlParams.get("stripe_success");

    if (hubspotSuccess === "true" || stripeSuccess === "true") {
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Re-check connections
      setTimeout(() => {
        checkConnections();
      }, 1000);
    }
  }, []);

  const checkConnections = async () => {
    setIsChecking(true);
    setIsTransitioning(true);
    try {
      const [hubspotStatus, stripeStatus] = await Promise.allSettled([
        hubSpotService.getHubSpotStatus(),
        stripeIntegrationService.getStripeStatus(),
      ]);

      const hubspotIsConnected =
        hubspotStatus.status === "fulfilled" &&
        hubspotStatus.value.data?.connected;
      const stripeIsConnected =
        stripeStatus.status === "fulfilled" &&
        stripeStatus.value.data?.connected;

      setHubspotConnected(hubspotIsConnected || false);
      setStripeConnected(stripeIsConnected || false);

      // Determine next step with smooth transition
      await new Promise((resolve) => setTimeout(resolve, 150));

      if (hubspotIsConnected && stripeIsConnected) {
        setCurrentStep("complete");
      } else if (!hubspotIsConnected && !stripeIsConnected) {
        setCurrentStep("select");
      } else if (!hubspotIsConnected) {
        setCurrentStep("hubspot");
      } else if (!stripeIsConnected) {
        setCurrentStep("stripe");
      }

      await new Promise((resolve) => setTimeout(resolve, 200));
      setIsTransitioning(false);
    } catch (error) {
      console.error("Failed to check connections:", error);
      showToast("error", "Failed to check connection status");
      setIsTransitioning(false);
    } finally {
      setIsChecking(false);
    }
  };

  const handleConnectHubspot = async () => {
    setIsConnectingHubspot(true);
    showLoader();
    try {
      const { data } = await hubSpotService.connectHubSpot();
      if (data.authorizationUrl) {
        window.location.href = data.authorizationUrl;
      }
    } catch (error: any) {
      console.error("Error connecting HubSpot:", error);
      showToast("error", error.message || "Failed to connect HubSpot");
      setIsConnectingHubspot(false);
      hideLoader();
    }
  };

  const handleConnectStripe = async () => {
    setIsConnectingStripe(true);
    showLoader();
    try {
      const { data } = await stripeIntegrationService.connectStripe();
      if (data.authorizationUrl) {
        window.location.href = data.authorizationUrl;
      }
    } catch (error: any) {
      console.error("Error connecting Stripe:", error);
      showToast("error", error.message || "Failed to connect Stripe");
      setIsConnectingStripe(false);
      hideLoader();
    }
  };

  const handleComplete = () => {
    // Navigate to the Contract Intelligence dashboard (to be created)
    navigate("/");
    showToast("success", "Contract Intelligence is now active!");
  };

  return (
    <div className="flex flex-col w-full min-h-screen bg-background-default dark:bg-background-dark text-text-primary dark:text-text-primaryDark relative overflow-hidden">
      {/* Subtle grid pattern */}
      <div className="subtle-grid"></div>

      <main className="flex-1 flex flex-col items-center justify-start px-6 pt-8 pb-4 relative z-10">
        <div className="max-w-2xl mx-auto w-full mt-8">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="flex justify-center mb-6">
              <div
                className={`p-4 rounded-xl ${
                  theme === "light"
                    ? "bg-background-panel border border-divider/50"
                    : "bg-secondary/10"
                }`}
              >
                <TrendingUp
                  className={`w-8 h-8 ${
                    theme === "light" ? "text-black" : "text-secondary"
                  }`}
                />
              </div>
            </div>
            <h1 className="text-3xl md:text-4xl font-semibold text-black dark:text-text-primaryDark mb-3 tracking-tight">
              Contract Intelligence Setup
            </h1>
            <p className="text-base text-black/60 dark:text-text-secondaryDark">
              Connect HubSpot and Stripe to enable revenue intelligence
            </p>
          </div>

          {/* Content Card */}
          <div className="bg-background-paper dark:bg-background-paperDark rounded-lg border border-black/8 dark:border-divider-dark p-8">
            <div
              className={`transition-opacity duration-300 ${
                isTransitioning ? "opacity-0" : "opacity-100"
              }`}
            >
              {isChecking ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-secondary mx-auto mb-4" />
                  <p className="text-sm text-black/60 dark:text-text-secondaryDark">
                    Checking connections...
                  </p>
                </div>
              ) : currentStep === "select" ? (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-semibold text-black dark:text-text-primaryDark mb-2">
                      Connect Your Tools
                    </h2>
                    <p className="text-sm text-black/60 dark:text-text-secondaryDark">
                      Connect HubSpot or Stripe to get started. You can connect
                      them in any order.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* HubSpot Card */}
                    <div
                      className={`p-6 rounded-xl border-2 transition-all ${
                        hubspotConnected
                          ? "border-green-500/50 bg-green-50/50 dark:bg-green-900/10"
                          : "border-divider dark:border-divider-dark hover:border-[#FF7A59]/50 bg-background-panel dark:bg-background-panelDark"
                      }`}
                    >
                      <div className="flex flex-col items-center text-center space-y-4">
                        <div className="relative">
                          <img
                            src={hubspotLogo}
                            alt="HubSpot"
                            className="h-12 w-auto object-contain"
                          />
                          {hubspotConnected && (
                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center border-2 border-white dark:border-background-paperDark z-10">
                              <CheckCircle className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-1">
                            HubSpot
                          </h3>
                          <p className="text-xs text-black/60 dark:text-text-secondaryDark">
                            Sync deals, contacts, and companies
                          </p>
                        </div>
                        {hubspotConnected ? (
                          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                            <CheckCircle className="w-5 h-5" />
                            <span className="text-sm font-medium">
                              Connected
                            </span>
                          </div>
                        ) : (
                          <button
                            onClick={handleConnectHubspot}
                            disabled={isConnectingHubspot}
                            className="w-full px-4 py-2.5 bg-[#FF7A59] text-white text-sm font-medium rounded-md hover:bg-[#FF7A59]/90 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
                          >
                            {isConnectingHubspot ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                <span>Connecting...</span>
                              </>
                            ) : (
                              <>
                                <span>Connect HubSpot</span>
                                <ArrowRight className="w-3.5 h-3.5" />
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Stripe Card */}
                    <div
                      className={`p-6 rounded-xl border-2 transition-all ${
                        stripeConnected
                          ? "border-green-500/50 bg-green-50/50 dark:bg-green-900/10"
                          : "border-divider dark:border-divider-dark hover:border-[#635BFF]/50 bg-background-panel dark:bg-background-panelDark"
                      }`}
                    >
                      <div className="flex flex-col items-center text-center space-y-4">
                        <div className="relative">
                          <img
                            src={stripeLogoPurple}
                            alt="Stripe"
                            className="h-12 w-auto object-contain"
                          />
                          {stripeConnected && (
                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center border-2 border-white dark:border-background-paperDark z-10">
                              <CheckCircle className="w-4 h-4 text-white" />
                            </div>
                          )}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-black dark:text-text-primaryDark mb-1">
                            Stripe
                          </h3>
                          <p className="text-xs text-black/60 dark:text-text-secondaryDark">
                            Sync subscriptions and payments
                          </p>
                        </div>
                        {stripeConnected ? (
                          <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                            <CheckCircle className="w-5 h-5" />
                            <span className="text-sm font-medium">
                              Connected
                            </span>
                          </div>
                        ) : (
                          <button
                            onClick={handleConnectStripe}
                            disabled={isConnectingStripe}
                            className="w-full px-4 py-2.5 bg-[#635BFF] text-white text-sm font-medium rounded-md hover:bg-[#635BFF]/90 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-sm"
                          >
                            {isConnectingStripe ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                <span>Connecting...</span>
                              </>
                            ) : (
                              <>
                                <span>Connect Stripe</span>
                                <ArrowRight className="w-3.5 h-3.5" />
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  {(hubspotConnected || stripeConnected) &&
                    !(hubspotConnected && stripeConnected) && (
                      <div className="pt-4 border-t border-black/8 dark:border-divider-dark text-center">
                        <p className="text-sm text-black/60 dark:text-text-secondaryDark mb-3">
                          {hubspotConnected
                            ? "Connect Stripe to complete setup"
                            : "Connect HubSpot to complete setup"}
                        </p>
                      </div>
                    )}
                </div>
              ) : currentStep === "hubspot" ? (
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-secondary/10 dark:bg-secondary/10 rounded-lg flex-shrink-0 flex items-center justify-center">
                      <img
                        src={hubspotLogo}
                        alt="HubSpot"
                        className="h-10 w-auto object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold text-black dark:text-text-primaryDark mb-2">
                        Connect HubSpot
                      </h2>
                      <p className="text-sm text-black/60 dark:text-text-secondaryDark mb-4">
                        Connect your HubSpot account to sync deals, contacts,
                        and companies with your revenue data.
                      </p>
                      {hubspotConnected ? (
                        <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                          <CheckCircle className="w-5 h-5" />
                          <span className="text-sm font-medium">Connected</span>
                        </div>
                      ) : (
                        <button
                          onClick={handleConnectHubspot}
                          disabled={isConnectingHubspot}
                          className="px-5 py-2.5 bg-black dark:bg-white text-white dark:text-black text-sm font-medium rounded-md hover:bg-black/90 dark:hover:bg-white/90 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
                        >
                          {isConnectingHubspot ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              <span>Connecting...</span>
                            </>
                          ) : (
                            <>
                              <span>Connect HubSpot</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  {hubspotConnected && !stripeConnected && (
                    <div className="pt-4 border-t border-black/8 dark:border-divider-dark">
                      <button
                        onClick={() => {
                          setIsTransitioning(true);
                          setTimeout(() => {
                            setCurrentStep("stripe");
                            setIsTransitioning(false);
                          }, 200);
                        }}
                        className="w-full px-5 py-2.5 bg-black dark:bg-white text-white dark:text-black text-sm font-medium rounded-md hover:bg-black/90 dark:hover:bg-white/90 active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-2 shadow-sm"
                      >
                        <span>Continue to Stripe</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              ) : currentStep === "stripe" ? (
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg flex-shrink-0 flex items-center justify-center">
                      <img
                        src={stripeLogoPurple}
                        alt="Stripe"
                        className="h-10 w-auto object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-xl font-semibold text-black dark:text-text-primaryDark mb-2">
                        Connect Stripe
                      </h2>
                      <p className="text-sm text-black/60 dark:text-text-secondaryDark mb-4">
                        Connect your Stripe account to sync subscriptions,
                        payments, and revenue metrics.
                      </p>
                      {stripeConnected ? (
                        <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                          <CheckCircle className="w-5 h-5" />
                          <span className="text-sm font-medium">Connected</span>
                        </div>
                      ) : (
                        <button
                          onClick={handleConnectStripe}
                          disabled={isConnectingStripe}
                          className="px-5 py-2.5 bg-black dark:bg-white text-white dark:text-black text-sm font-medium rounded-md hover:bg-black/90 dark:hover:bg-white/90 active:scale-[0.98] transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
                        >
                          {isConnectingStripe ? (
                            <>
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              <span>Connecting...</span>
                            </>
                          ) : (
                            <>
                              <span>Connect Stripe</span>
                              <ArrowRight className="w-3.5 h-3.5" />
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                  {stripeConnected && !hubspotConnected && (
                    <div className="pt-4 border-t border-black/8 dark:border-divider-dark">
                      <button
                        onClick={() => {
                          setIsTransitioning(true);
                          setTimeout(() => {
                            setCurrentStep("hubspot");
                            setIsTransitioning(false);
                          }, 200);
                        }}
                        className="w-full px-5 py-2.5 bg-black dark:bg-white text-white dark:text-black text-sm font-medium rounded-md hover:bg-black/90 dark:hover:bg-white/90 active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-2 shadow-sm"
                      >
                        <span>Continue to HubSpot</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                  {stripeConnected && hubspotConnected && (
                    <div className="pt-4 border-t border-black/8 dark:border-divider-dark">
                      <button
                        onClick={handleComplete}
                        className="w-full px-5 py-2.5 bg-black dark:bg-white text-white dark:text-black text-sm font-medium rounded-md hover:bg-black/90 dark:hover:bg-white/90 active:scale-[0.98] transition-all duration-150 flex items-center justify-center gap-2 shadow-sm"
                      >
                        <span>Complete Setup</span>
                        <CheckCircle className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              ) : currentStep === "complete" ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-white" />
                  </div>
                  <h2 className="text-2xl font-semibold text-black dark:text-text-primaryDark mb-3">
                    All Set!
                  </h2>
                  <p className="text-sm text-black/60 dark:text-text-secondaryDark mb-6">
                    HubSpot and Stripe are connected. Contract Intelligence is
                    now active and ready to use.
                  </p>
                  <button
                    onClick={handleComplete}
                    className="px-5 py-2.5 bg-black dark:bg-white text-white dark:text-black text-sm font-medium rounded-md hover:bg-black/90 dark:hover:bg-white/90 active:scale-[0.98] transition-all duration-150 shadow-sm"
                  >
                    Go to Dashboard
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ContractIntelligenceSetup;
